import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Target, Edit3, TrendingUp } from 'lucide-react';
import { tradingService } from '../../services/trading';
import { ProfitLimitDialog } from './ProfitLimitDialog';

/**
 * Profit Target Section for Settings page.
 * Presents the daily profit threshold as a global setting.
 * Behind the scenes, syncs the value across all algorithm configs.
 */
export function ProfitTargetSection() {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Get all algorithm configs
  const { data: configs = [], isLoading: isLoadingConfigs } = useQuery({
    queryKey: ['algorithmConfigs'],
    queryFn: () => tradingService.getAlgorithmConfigs(),
  });

  // Get circuit breaker status for the active config (for progress data)
  const activeConfig = configs.find(c => c.isActive);

  const { data: cbStatus } = useQuery({
    queryKey: ['circuitBreakerStatus', activeConfig?.id],
    queryFn: async () => {
      if (!activeConfig) return null;
      return tradingService.getCircuitBreakerStatus(activeConfig.id).catch(() => null);
    },
    enabled: !!activeConfig,
    refetchInterval: 10000,
  });

  // Get the current profit limit from the first config (they should all be synced)
  const currentLimit = configs.length > 0
    ? parseFloat(String(configs[0].dailyProfitLimit || 0))
    : 0;
  const isEnabled = currentLimit > 0;

  // Get the current block mode from the first config
  const currentBlockMode = configs.length > 0
    ? (configs[0].circuitBreakerBlockMode || 'buy_only')
    : 'buy_only';

  // Mutation to update profit limit on all configs
  const updateLimitMutation = useMutation({
    mutationFn: (limit: number) => tradingService.updateDailyProfitLimitAll(configs, limit),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['algorithmConfigs'] });
      queryClient.invalidateQueries({ queryKey: ['circuitBreakerStatus'] });
      setIsDialogOpen(false);
    },
    onError: (error: any) => {
      console.error('Failed to update profit limit:', error);
      alert(error.message || 'Failed to update profit target');
    },
  });

  // Mutation to update block mode on all configs
  const updateBlockModeMutation = useMutation({
    mutationFn: (mode: 'buy_only' | 'all') => tradingService.updateBlockModeAll(configs, mode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['algorithmConfigs'] });
      queryClient.invalidateQueries({ queryKey: ['circuitBreakerStatus'] });
    },
    onError: (error: any) => {
      console.error('Failed to update block mode:', error);
      alert(error.message || 'Failed to update block mode');
    },
  });

  // Quick toggle: enable with $400 default or disable
  const handleQuickToggle = () => {
    if (isEnabled) {
      updateLimitMutation.mutate(0);
    } else {
      updateLimitMutation.mutate(400);
    }
  };

  // No configs - show setup message
  if (!isLoadingConfigs && configs.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-full">
            <Target className="w-8 h-8 text-gray-400" />
          </div>
        </div>
        <p className="text-gray-500 dark:text-gray-400">
          No webhook configurations found.
        </p>
        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
          Set up webhook trading to enable profit targets.
        </p>
      </div>
    );
  }

  if (isLoadingConfigs) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  // Calculate progress for the bar
  const globalPnl = cbStatus?.globalDailyPnl ?? 0;
  const progressPct = isEnabled && currentLimit > 0
    ? Math.min(100, Math.max(0, (globalPnl / currentLimit) * 100))
    : 0;
  const isTriggered = cbStatus?.isTriggered && globalPnl >= currentLimit && isEnabled;

  return (
    <div className="space-y-5">
      {/* Status Card */}
      <motion.div
        className={`rounded-xl p-5 border-2 transition-colors ${
          isTriggered
            ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-900'
            : isEnabled
              ? 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
              : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
        }`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Top Row: Toggle + Amount */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-full ${
              isTriggered
                ? 'bg-green-100 dark:bg-green-900/50'
                : isEnabled
                  ? 'bg-green-100 dark:bg-green-900/50'
                  : 'bg-gray-100 dark:bg-gray-700'
            }`}>
              <Target className={`w-5 h-5 ${
                isTriggered || isEnabled ? 'text-green-600 dark:text-green-400' : 'text-gray-400'
              }`} />
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {isTriggered ? 'Profit Target Reached' : isEnabled ? 'Profit Target Active' : 'Profit Target Disabled'}
              </div>
              {isEnabled && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Buys pause at ${currentLimit.toLocaleString()} daily profit
                </div>
              )}
            </div>
          </div>

          {/* Toggle */}
          <button
            onClick={handleQuickToggle}
            disabled={updateLimitMutation.isPending}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors disabled:opacity-50 ${
              isEnabled ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                isEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Amount Display + Edit */}
        {isEnabled && (
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-baseline space-x-2">
              <span className="text-3xl font-bold text-gray-900 dark:text-white">
                ${currentLimit.toLocaleString()}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                daily target
              </span>
            </div>
            <button
              onClick={() => setIsDialogOpen(true)}
              className="flex items-center space-x-1 px-3 py-1.5 text-sm text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
            >
              <Edit3 className="w-3.5 h-3.5" />
              <span>Edit</span>
            </button>
          </div>
        )}

        {/* Progress Bar (only when enabled) */}
        {isEnabled && (
          <div>
            <div className="flex items-center justify-between mb-1.5 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center space-x-1">
                <TrendingUp className="w-3.5 h-3.5" />
                <span>Today&apos;s Progress</span>
              </span>
              <span className={isTriggered ? 'text-green-600 dark:text-green-400 font-semibold' : ''}>
                ${globalPnl >= 0 ? '' : '-'}${Math.abs(globalPnl).toFixed(2)} / ${currentLimit.toLocaleString()}
              </span>
            </div>
            <div className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <motion.div
                className={`h-full rounded-full ${
                  isTriggered
                    ? 'bg-green-500 shadow-lg shadow-green-500/30'
                    : progressPct >= 80
                      ? 'bg-amber-500'
                      : 'bg-green-500'
                }`}
                initial={{ width: 0 }}
                animate={{ width: `${Math.max(0, progressPct)}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              />
            </div>
            {isTriggered && (
              <motion.p
                className="mt-2 text-xs text-green-600 dark:text-green-400 font-medium"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                New buys paused until midnight PT. Open positions still close normally.
              </motion.p>
            )}
          </div>
        )}

        {/* Description when disabled */}
        {!isEnabled && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Set a daily profit target to automatically pause buying when you hit your goal. Open positions still close normally via sell rules.
          </p>
        )}
      </motion.div>

      {/* Block Mode Selector */}
      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
        <div>
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            When Limits Trigger
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {currentBlockMode === 'buy_only'
              ? 'Only new buys are blocked. Sells still close positions.'
              : 'All signals blocked, including sells.'}
          </div>
        </div>
        <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
          <button
            onClick={() => updateBlockModeMutation.mutate('buy_only')}
            disabled={updateBlockModeMutation.isPending}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${
              currentBlockMode === 'buy_only'
                ? 'bg-green-600 text-white'
                : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
            }`}
          >
            BUY Only
          </button>
          <button
            onClick={() => updateBlockModeMutation.mutate('all')}
            disabled={updateBlockModeMutation.isPending}
            className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-gray-300 dark:border-gray-600 ${
              currentBlockMode === 'all'
                ? 'bg-red-600 text-white'
                : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
            }`}
          >
            All Signals
          </button>
        </div>
      </div>

      {/* Profit Limit Dialog */}
      <ProfitLimitDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirm={(limit) => updateLimitMutation.mutate(limit)}
        currentLimit={currentLimit}
        isLoading={updateLimitMutation.isPending}
      />
    </div>
  );
}

